/**
 * Synchronizacja ze starym systemem — ekran KLIENTA (ADR-0111, pakiet 9).
 *
 * ── CO TU JEST, A CZEGO NIE ──
 *
 * Klient widzi stan („kiedy ostatnio coś przyszło, ile czeka na decyzję") i rozstrzyga
 * rozbieżności. NIE ma tu konfiguracji połączenia, mapowania pól ani uruchamiania przebiegów —
 * te zostają w panelu ITE. Podział nie jest kosmetyczny: mapowanie źle ustawione przepisuje
 * tysiące rekordów, a uruchomienie przebiegu sięga do produkcyjnej bazy klienta.
 *
 * Tutaj jest odwrotnie: pytanie „kontrahent Kowalski, termin płatności — u nas 14 dni, w starym
 * systemie 30; która wartość jest prawdziwa" zna osoba, która ten rekord prowadzi. Przy pracy
 * równoległej przez kilka tygodni takich pytań są dziesiątki tygodniowo, więc przepuszczanie
 * każdego przez wdrożeniowca byłoby wąskim gardłem z własnej ręki.
 *
 * ── DWA PRAWA ──
 *
 * Podgląd i rozstrzyganie są osobne. Kto ma tylko podgląd, widzi listę z nieaktywnymi
 * przyciskami — a nie pustą stronę. To celowe: „nie mam prawa" i „nic nie czeka" to dwie różne
 * informacje, a pomylenie ich kończy się zgłoszeniem do wsparcia.
 *
 * ── DLACZEGO „NASZA WARTOŚĆ" BYWA NIEAKTYWNA ──
 *
 * Wybór „nasza wartość jest prawdziwa" wymaga zapisu do starego systemu. Dopóki go nie ma,
 * przycisk jest wyłączony z wyjaśnieniem, zamiast przyjmować decyzję, której nie da się
 * wykonać — i która przy najbliższym sprawdzeniu zostałaby po cichu odwrócona.
 */

import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Alert } from '@/components/Alert';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Skeleton } from '@/components/Skeleton';
import { useToast } from '@/contexts/ToastContext';
import { usePermission } from '@/hooks/usePermission';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';

export const LEGACY_SYNC_VIEW = 'crm_core.legacy_sync.view';
export const LEGACY_SYNC_RESOLVE = 'crm_core.legacy_sync.resolve';

interface ConflictRow {
  id: string;
  entity: string;
  sourceRecordId: string;
  targetField: string;
  sourceField: string;
  ourValue: string | null;
  theirValue: string | null;
  baselineValue: string | null;
  detectedAt: string;
}

interface SyncStatus {
  configured: boolean;
  paused?: boolean;
  lastSyncAt?: string | null;
  pending: number;
  byEntity: Record<string, number>;
}

type Choice = 'keep_theirs' | 'keep_ours' | 'ignore';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pl-PL');
  } catch {
    return iso;
  }
}

export function LegacySyncTab() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const mozeRozstrzygac = usePermission(LEGACY_SYNC_RESOLVE);

  const status = useQuery({
    queryKey: ['legacy-sync', 'status'],
    queryFn: async () => (await api.get<SyncStatus>('/legacy-sync/status')).data,
  });

  const lista = useQuery({
    queryKey: ['legacy-sync', 'conflicts'],
    queryFn: async () =>
      (await api.get<{ conflicts: ConflictRow[]; total: number }>('/legacy-sync/conflicts')).data,
    enabled: status.data?.configured === true,
  });

  const rozstrzygnij = useMutation({
    mutationFn: async (vars: { id: string; choice: Choice }) => {
      await api.post(`/legacy-sync/conflicts/${vars.id}/resolve`, { choice: vars.choice });
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.legacySync.resolved') });
      void queryClient.invalidateQueries({ queryKey: ['legacy-sync'] });
    },
    onError: (err) =>
      toast.show({
        variant: 'error',
        title: t(
          `tenant.legacySync.errors.${extractErrorCode(err)}`,
          t('tenant.legacySync.failed'),
        ),
      }),
  });

  if (status.isLoading) return <Skeleton className="h-40 w-full" />;

  // Większość tenantów nigdy nie będzie miała starego systemu — mówimy to wprost, zamiast
  // pokazywać pusty ekran, który wygląda na awarię.
  if (!status.data?.configured) {
    return <Alert tone="info">{t('tenant.legacySync.notConfigured')}</Alert>;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-[var(--text-secondary)]">{t('tenant.legacySync.lastSync')}</span>
          <span className="font-medium">
            {status.data.lastSyncAt
              ? formatDateTime(status.data.lastSyncAt)
              : t('tenant.legacySync.never')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[var(--text-secondary)]">{t('tenant.legacySync.pending')}</span>
          <Badge color={status.data.pending > 0 ? 'amber' : 'gray'}>{status.data.pending}</Badge>
        </div>
        {status.data.paused && <Alert tone="warning">{t('tenant.legacySync.paused')}</Alert>}
      </div>

      {!mozeRozstrzygac.allowed && <Alert tone="info">{t('tenant.legacySync.viewOnly')}</Alert>}

      {lista.isLoading && <Skeleton className="h-24 w-full" />}

      {lista.data && lista.data.conflicts.length === 0 && (
        <Alert tone="success">{t('tenant.legacySync.allClear')}</Alert>
      )}

      {lista.data?.conflicts.map((k) => (
        <div
          key={k.id}
          className="flex flex-col gap-3 rounded-md border border-[var(--border-primary)] p-4"
        >
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-sm font-medium">
              {t(`tenant.legacySync.entity.${k.entity}`, k.entity)}
            </span>
            <span className="text-sm">{k.targetField}</span>
            <span className="text-xs text-[var(--text-secondary)]">
              {t('tenant.legacySync.record', { id: k.sourceRecordId })}
            </span>
          </div>

          <dl className="grid gap-3 sm:grid-cols-3">
            <Wartosc etykieta={t('tenant.legacySync.ours')} wartosc={k.ourValue} />
            <Wartosc etykieta={t('tenant.legacySync.theirs')} wartosc={k.theirValue} />
            <Wartosc etykieta={t('tenant.legacySync.baseline')} wartosc={k.baselineValue} />
          </dl>

          <div className="flex flex-wrap justify-end gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={!mozeRozstrzygac.allowed || rozstrzygnij.isPending}
              onClick={() => rozstrzygnij.mutate({ id: k.id, choice: 'ignore' })}
            >
              {t('tenant.legacySync.choice.ignore')}
            </Button>
            {/* „Nasza wartość" wymaga zapisu do starego systemu — jeszcze go nie ma. */}
            <Button
              size="sm"
              variant="secondary"
              disabled
              title={t('tenant.legacySync.choice.keepOursUnavailable')}
            >
              {t('tenant.legacySync.choice.keepOurs')}
            </Button>
            <Button
              size="sm"
              disabled={!mozeRozstrzygac.allowed || rozstrzygnij.isPending}
              onClick={() => rozstrzygnij.mutate({ id: k.id, choice: 'keep_theirs' })}
            >
              {t('tenant.legacySync.choice.keepTheirs')}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Jedna z trzech wartości. Pustą pokazujemy jawnie — „brak" też jest odpowiedzią. */
function Wartosc({ etykieta, wartosc }: { etykieta: string; wartosc: string | null }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-w-0 flex-col">
      <dt className="text-xs text-[var(--text-secondary)]">{etykieta}</dt>
      <dd className="break-words text-sm">
        {wartosc ?? (
          <span className="text-[var(--text-secondary)]">{t('tenant.legacySync.emptyValue')}</span>
        )}
      </dd>
    </div>
  );
}
